import { useState, useEffect } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Table,
  Badge,
  Card,
  Group,
  Stack,
  Text,
  Title,
  Select,
  TextInput,
  Pagination,
  Button,
  Drawer,
  Textarea,
  ActionIcon,
  Divider,
  SimpleGrid,
  Paper,
  LoadingOverlay,
  Tabs,
  Anchor,
  Tooltip,
  Container,
  Box,
} from '@mantine/core';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  IconSearch,
  IconCreditCard,
  IconMessage,
  IconBrandWhatsapp,
  IconSend,
  IconTruck,
  IconEye,
  IconReload,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

type OrderStatus = 'PENDING' | 'CONFIRMED' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED';
type PaymentStatus = 'UNPAID' | 'PARTIAL' | 'PAID' | 'REFUNDED' | 'FAILED';

interface OrderItem {
  retail_product_id: string;
  name: string;
  quantity: number;
  item_price: number;
}

interface Order {
  id: string;
  orderId: string;
  vendorId: number;
  contactId: number;
  customerPhone: string;
  customerName: string;
  totalAmount: string;
  taxAmount: string;
  shippingAmount: string;
  discountAmount: string;
  currency: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  items: OrderItem[];
  metaData: unknown;
  createdAt: string;
  updatedAt: string;
  contact: {
    id: number;
    uid: string;
    firstName: string | null;
    lastName: string | null;
    waId: string;
  };
  payments?: unknown[];
}

function getOrderStatusColor(status: OrderStatus) {
  switch (status) {
    case 'PENDING':
      return 'yellow';
    case 'CONFIRMED':
      return 'teal';
    case 'PROCESSING':
      return 'blue';
    case 'SHIPPED':
      return 'indigo';
    case 'DELIVERED':
      return 'green';
    case 'CANCELLED':
      return 'red';
    default:
      return 'gray';
  }
}

function getPaymentStatusColor(status: PaymentStatus) {
  switch (status) {
    case 'PAID':
      return 'green';
    case 'PARTIAL':
      return 'orange';
    case 'UNPAID':
      return 'gray';
    case 'REFUNDED':
      return 'cyan';
    case 'FAILED':
      return 'red';
    default:
      return 'gray';
  }
}

function OrderDashboard() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [paymentFilter, setPaymentFilter] = useState<string>('ALL');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [drawerOpened, setDrawerOpened] = useState(false);

  // Filters and Pagination query
  const { data, isLoading, refetch } = useQuery<{
    data: Order[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }>({
    queryKey: ['orders', page, search, statusFilter, paymentFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '10');
      if (search) params.set('search', search);
      if (statusFilter !== 'ALL') params.set('status', statusFilter);
      if (paymentFilter !== 'ALL') params.set('paymentStatus', paymentFilter);

      const res = await fetch(`/api/orders?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch orders');
      return res.json();
    },
  });

  const handleOpenDrawer = (orderId: string) => {
    setSelectedOrderId(orderId);
    setDrawerOpened(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpened(false);
    setSelectedOrderId(null);
  };

  const rows = data?.data.map((order) => {
    const formattedTotal = `${Number(order.totalAmount).toFixed(3)} ${order.currency}`;
    const date = new Date(order.createdAt).toLocaleDateString(undefined, {
      dateStyle: 'medium',
    });

    return (
      <Table.Tr
        key={order.id}
        style={{ cursor: 'pointer' }}
        onClick={() => handleOpenDrawer(order.id)}
      >
        <Table.Td>
          <Text fw={700} c="blue.6">
            {order.orderId}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{date}</Text>
        </Table.Td>
        <Table.Td>
          <Stack gap={2}>
            <Text size="sm" fw={500}>
              {order.customerName}
            </Text>
            <Text size="xs" c="dimmed">
              +{order.customerPhone}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Text size="sm" fw={700}>
            {formattedTotal}
          </Text>
        </Table.Td>
        <Table.Td>
          <Badge color={getOrderStatusColor(order.status)} variant="light">
            {order.status}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge color={getPaymentStatusColor(order.paymentStatus)} variant="light">
            {order.paymentStatus}
          </Badge>
        </Table.Td>
        <Table.Td onClick={(e) => e.stopPropagation()}>
          <Tooltip label="View Details">
            <ActionIcon variant="subtle" color="blue" onClick={() => handleOpenDrawer(order.id)}>
              <IconEye size={16} />
            </ActionIcon>
          </Tooltip>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Container fluid p="md">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Stack gap={2}>
            <Title order={2}>WhatsApp Orders</Title>
            <Text size="sm" c="dimmed">
              Manage incoming catalog orders and billing requests received from WhatsApp
            </Text>
          </Stack>
          <Button variant="light" leftSection={<IconReload size={16} />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Group>

        <Card withBorder radius="md" p="md">
          <Stack gap="md">
            {/* Quick Status Tabs */}
            <Tabs
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v || 'ALL');
                setPage(1);
              }}
            >
              <Tabs.List>
                <Tabs.Tab value="ALL">All Orders</Tabs.Tab>
                <Tabs.Tab value="PENDING">Pending</Tabs.Tab>
                <Tabs.Tab value="CONFIRMED">Confirmed</Tabs.Tab>
                <Tabs.Tab value="PROCESSING">Processing</Tabs.Tab>
                <Tabs.Tab value="SHIPPED">Shipped</Tabs.Tab>
                <Tabs.Tab value="DELIVERED">Delivered</Tabs.Tab>
                <Tabs.Tab value="CANCELLED">Cancelled</Tabs.Tab>
              </Tabs.List>
            </Tabs>

            {/* Filter controls */}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput
                placeholder="Search by Order ID, Customer Name or Phone..."
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
              <Select
                placeholder="Filter by Payment Status"
                value={paymentFilter}
                onChange={(v) => {
                  setPaymentFilter(v || 'ALL');
                  setPage(1);
                }}
                data={[
                  { value: 'ALL', label: 'All Payment Statuses' },
                  { value: 'UNPAID', label: 'Unpaid' },
                  { value: 'PARTIAL', label: 'Partial' },
                  { value: 'PAID', label: 'Paid' },
                  { value: 'REFUNDED', label: 'Refunded' },
                  { value: 'FAILED', label: 'Failed' },
                ]}
              />
            </SimpleGrid>
          </Stack>
        </Card>

        {/* Table card */}
        <Paper withBorder radius="md" style={{ position: 'relative', overflow: 'hidden' }}>
          <LoadingOverlay visible={isLoading} overlayProps={{ blur: 1 }} />
          <Box style={{ overflowX: 'auto' }}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Order ID</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Customer</Table.Th>
                  <Table.Th>Total</Table.Th>
                  <Table.Th>Order Status</Table.Th>
                  <Table.Th>Payment Status</Table.Th>
                  <Table.Th style={{ width: 80 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows?.length ? (
                  rows
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={7} align="center">
                      <Text c="dimmed" py="xl">
                        No orders found matching your criteria
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Box>
        </Paper>

        {/* Pagination */}
        {data && data.pagination.totalPages > 1 && (
          <Group justify="center">
            <Pagination value={page} onChange={setPage} total={data.pagination.totalPages} />
          </Group>
        )}
      </Stack>

      {/* Details Drawer */}
      <OrderDetailDrawer
        orderId={selectedOrderId}
        opened={drawerOpened}
        onClose={handleCloseDrawer}
        onUpdateSuccess={() => refetch()}
      />
    </Container>
  );
}

// Sub-component for Order Details
function OrderDetailDrawer({
  orderId,
  opened,
  onClose,
  onUpdateSuccess,
}: {
  orderId: string | null;
  opened: boolean;
  onClose: () => void;
  onUpdateSuccess: () => void;
}) {
  const [customMessage, setCustomMessage] = useState('');
  const [status, setStatus] = useState<OrderStatus>('PENDING');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('UNPAID');
  const [trackingId, setTrackingId] = useState('');

  // Fetch Order Details
  const {
    data: order,
    isLoading,
    refetch,
  } = useQuery<Order>({
    queryKey: ['order', orderId],
    queryFn: async () => {
      if (!orderId) return null;
      const res = await fetch(`/api/orders/${orderId}`);
      if (!res.ok) throw new Error('Failed to fetch order details');
      return res.json();
    },
    enabled: !!orderId,
  });

  // Sync internal state when order details load/change
  useEffect(() => {
    if (order) {
      setStatus(order.status);
      setPaymentStatus(order.paymentStatus);
    }
  }, [order]);

  // Update Status Mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (params: {
      status?: OrderStatus;
      paymentStatus?: PaymentStatus;
      trackingId?: string;
    }) => {
      const res = await fetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!res.ok) throw new Error('Failed to update order status');
      return res.json();
    },
    onSuccess: () => {
      notifications.show({ message: 'Order status updated successfully', color: 'green' });
      onUpdateSuccess();
      refetch();
    },
    onError: (err) => {
      notifications.show({ message: err.message, color: 'red' });
    },
  });

  // Send Payment Link Mutation
  const sendPaymentLinkMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/orders/${orderId}/send-payment-link`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to send payment link');
      return res.json();
    },
    onSuccess: () => {
      notifications.show({ message: 'Payment link sent to customer via WhatsApp', color: 'green' });
      refetch();
    },
    onError: (err) => {
      notifications.show({ message: err.message, color: 'red' });
    },
  });

  // Send Custom Text Message Mutation via tRPC
  const sendTextMutation = trpc.whatsapp.send.text.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Message sent successfully', color: 'green' });
      setCustomMessage('');
    },
    onError: (err) => {
      notifications.show({ message: err.message, color: 'red' });
    },
  });

  const handleUpdateStatus = () => {
    updateStatusMutation.mutate({
      status,
      paymentStatus,
      trackingId: status === 'SHIPPED' ? trackingId : undefined,
    });
  };

  const handleSendCustomMessage = () => {
    if (!order || !customMessage.trim()) return;
    sendTextMutation.mutate({
      to: order.customerPhone,
      body: customMessage,
    });
  };

  if (!orderId) return null;

  const formattedTotal = order ? `${Number(order.totalAmount).toFixed(3)} ${order.currency}` : '';
  const formattedSubtotal = order
    ? `${(Number(order.totalAmount) - Number(order.taxAmount) - Number(order.shippingAmount) + Number(order.discountAmount)).toFixed(3)} ${order.currency}`
    : '';

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        order ? (
          <Group gap="sm">
            <Title order={4}>Order Details — {order.orderId}</Title>
            <Badge color={getOrderStatusColor(order.status)} variant="light">
              {order.status}
            </Badge>
            <Badge color={getPaymentStatusColor(order.paymentStatus)} variant="light">
              {order.paymentStatus}
            </Badge>
          </Group>
        ) : (
          'Loading...'
        )
      }
      position="right"
      size="xl"
      styles={{ body: { height: 'calc(100vh - 60px)', overflowY: 'auto' } }}
    >
      <Box style={{ position: 'relative', height: '100%' }}>
        <LoadingOverlay visible={isLoading} overlayProps={{ blur: 1 }} />

        {order && (
          <Stack gap="lg" py="md">
            {/* Customer Info Section */}
            <Paper withBorder p="md" radius="md">
              <Stack gap="xs">
                <Text fw={700} size="sm">
                  Customer Information
                </Text>
                <Divider />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">
                      Name
                    </Text>
                    <Text size="sm" fw={500}>
                      {order.customerName}
                    </Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">
                      Phone Number
                    </Text>
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        +{order.customerPhone}
                      </Text>
                      <Tooltip label="Chat on WhatsApp">
                        <Anchor
                          href={`https://wa.me/${order.customerPhone}`}
                          target="_blank"
                          c="green"
                        >
                          <IconBrandWhatsapp size={16} />
                        </Anchor>
                      </Tooltip>
                    </Group>
                  </Stack>
                </SimpleGrid>
                <Group justify="flex-end" mt="xs">
                  <Link
                    to="/vendor/chat"
                    search={{ contactId: order.contactId }}
                    style={{ textDecoration: 'none' }}
                  >
                    <Button variant="light" leftSection={<IconMessage size={14} />} size="xs">
                      Open in Chat Inbox
                    </Button>
                  </Link>
                </Group>
              </Stack>
            </Paper>

            {/* Shopping Cart Items */}
            <Paper withBorder p="md" radius="md">
              <Stack gap="xs">
                <Text fw={700} size="sm">
                  Itemized Cart
                </Text>
                <Divider />
                <Table verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Product / ID</Table.Th>
                      <Table.Th>Price</Table.Th>
                      <Table.Th style={{ width: 85 }}>Quantity</Table.Th>
                      <Table.Th align="right">Total</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {order.items.map((item, idx) => (
                      <Table.Tr key={idx}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.name || `Product ${item.retail_product_id}`}
                          </Text>
                          <Text size="xs" c="dimmed">
                            ID: {item.retail_product_id}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {Number(item.item_price).toFixed(3)} {order.currency}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{item.quantity}</Text>
                        </Table.Td>
                        <Table.Td align="right">
                          <Text size="sm" fw={700}>
                            {(Number(item.item_price) * item.quantity).toFixed(3)} {order.currency}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Paper>

            {/* Pricing Summary Card */}
            <Paper withBorder p="md" radius="md" bg="gray.0">
              <Stack gap="xs">
                <Text fw={700} size="sm">
                  Pricing Summary
                </Text>
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Subtotal
                  </Text>
                  <Text size="sm">{formattedSubtotal}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Tax
                  </Text>
                  <Text size="sm">
                    {Number(order.taxAmount).toFixed(3)} {order.currency}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Shipping
                  </Text>
                  <Text size="sm">
                    {Number(order.shippingAmount).toFixed(3)} {order.currency}
                  </Text>
                </Group>
                {Number(order.discountAmount) > 0 && (
                  <Group justify="space-between">
                    <Text size="sm" c="red">
                      Discount
                    </Text>
                    <Text size="sm" c="red">
                      -{Number(order.discountAmount).toFixed(3)} {order.currency}
                    </Text>
                  </Group>
                )}
                <Divider />
                <Group justify="space-between">
                  <Text fw={700} size="md">
                    Grand Total
                  </Text>
                  <Text fw={700} size="md" c="blue.7">
                    {formattedTotal}
                  </Text>
                </Group>
              </Stack>
            </Paper>

            {/* Action Panel */}
            <Paper withBorder p="md" radius="md">
              <Stack gap="md">
                <Text fw={700} size="sm">
                  Order Management Actions
                </Text>
                <Divider />

                {/* Status Update Fields */}
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <Select
                    label="Order Status"
                    value={status}
                    onChange={(v) => setStatus(v as OrderStatus)}
                    data={[
                      { value: 'PENDING', label: 'Pending' },
                      { value: 'CONFIRMED', label: 'Confirmed' },
                      { value: 'PROCESSING', label: 'Processing' },
                      { value: 'SHIPPED', label: 'Shipped' },
                      { value: 'DELIVERED', label: 'Delivered' },
                      { value: 'CANCELLED', label: 'Cancelled' },
                    ]}
                  />
                  <Select
                    label="Payment Status"
                    value={paymentStatus}
                    onChange={(v) => setPaymentStatus(v as PaymentStatus)}
                    data={[
                      { value: 'UNPAID', label: 'Unpaid' },
                      { value: 'PARTIAL', label: 'Partial' },
                      { value: 'PAID', label: 'Paid' },
                      { value: 'REFUNDED', label: 'Refunded' },
                      { value: 'FAILED', label: 'Failed' },
                    ]}
                  />
                </SimpleGrid>

                {/* Optional Tracking ID for Shipped */}
                {status === 'SHIPPED' && (
                  <TextInput
                    label="Tracking ID / Shipping Reference"
                    placeholder="Enter carrier tracking number..."
                    leftSection={<IconTruck size={16} />}
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                  />
                )}

                <Button
                  color="blue"
                  onClick={handleUpdateStatus}
                  loading={updateStatusMutation.isPending}
                >
                  Update Order & Send WhatsApp Status Notification
                </Button>

                <Divider my="xs" label="Billing & Payments" labelPosition="center" />

                {/* Generate & Send Payment Link */}
                <Button
                  variant="outline"
                  color="green"
                  leftSection={<IconCreditCard size={16} />}
                  onClick={() => sendPaymentLinkMutation.mutate()}
                  loading={sendPaymentLinkMutation.isPending}
                  disabled={order.paymentStatus === 'PAID'}
                >
                  Send Billing Payment Link via WhatsApp
                </Button>
              </Stack>
            </Paper>

            {/* Direct Message Thread Ingest */}
            <Paper withBorder p="md" radius="md">
              <Stack gap="xs">
                <Text fw={700} size="sm">
                  Send Quick Reply
                </Text>
                <Divider />
                <Textarea
                  placeholder="Type a message to send directly to the customer's WhatsApp thread..."
                  minRows={3}
                  maxRows={6}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                />
                <Group justify="flex-end">
                  <Button
                    color="teal"
                    rightSection={<IconSend size={14} />}
                    onClick={handleSendCustomMessage}
                    loading={sendTextMutation.isPending}
                    disabled={!customMessage.trim()}
                  >
                    Send Message
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

export const Route = createFileRoute('/vendor/orders')({ component: OrderDashboard });
